import { createFileRoute, Link, Outlet } from "@tanstack/react-router";
import { ConnectorPageColumn } from "@/components/connectors/connector-page-shell";
import {
	DETAIL_CRUMB_LINK_CLASS,
	DetailBreadcrumb,
	DetailHeader,
} from "@/components/connectors/detail-header";
import { ConnectionCreate } from "@/components/entity-tabs/connections-tab/connection-create";
import { OwnerWorkspaceGate } from "@/components/owner-workspace-gate";
import { FormLoadingShell } from "@/components/ui/form-loading-shell";
import { useConnections } from "@/lib/api/connections";
import { OWNER_SECTIONS } from "@/lib/owner-sections";

// Pure layout: the actual pages live in the child routes `create/index.tsx`
// (bare /create picker) and `create.$connectorKey.tsx` (deep-linked). Rendering
// its own UI here would shadow the child, so it only hosts the <Outlet/>.
export const Route = createFileRoute("/$owner/connectors/create")({
	component: () => <Outlet />,
});

export function ConnectionsCreateRouteContent({
	owner,
	initialConnectorKey,
}: {
	owner: string;
	initialConnectorKey?: string;
}) {
	return (
		<OwnerWorkspaceGate owner={owner}>
			{(props) => (
				<ConnectionsCreateInner
					ownerSlug={props.ownerSlug}
					organizationId={props.organizationId}
					initialConnectorKey={initialConnectorKey}
				/>
			)}
		</OwnerWorkspaceGate>
	);
}

function ConnectionsCreateInner({
	ownerSlug,
	organizationId,
	initialConnectorKey,
}: {
	ownerSlug: string;
	organizationId: string | null;
	initialConnectorKey?: string;
}) {
	const { data: connections = [] } = useConnections(organizationId ?? "");
	// Page padding comes from the connectors layout.
	return (
		<ConnectorPageColumn className="flex flex-1 flex-col gap-6">
			{/* Same header as every other page under connectors: the trail carries
			    the way back, so there is no second back affordance in the title. */}
			<DetailHeader
				breadcrumb={
					<DetailBreadcrumb
						crumbs={[
							<Link
								key="connectors"
								to="/$owner/connectors"
								params={{ owner: ownerSlug }}
								search={{}}
								className={DETAIL_CRUMB_LINK_CLASS}
							>
								Connectors
							</Link>,
						]}
					/>
				}
				icon={<OWNER_SECTIONS.connectors.icon className="h-6 w-6" />}
				title="Add a connection"
				caption="Pick a connector from the catalog or drop in an MCP URL — Lobu handles auth and brings the data into memory."
			/>
			{/* Form body renders a label+input skeleton while workspace is
			 *  in flight (ConnectionCreate's connector picker depends on
			 *  orgId). Page chrome above paints from frame one regardless. */}
			{organizationId ? (
				<ConnectionCreate
					key={initialConnectorKey ?? "catalog"}
					ownerSlug={ownerSlug}
					organizationId={organizationId}
					connections={connections}
					initialConnectorKey={initialConnectorKey}
				/>
			) : (
				<div className="max-w-3xl">
					<FormLoadingShell rows={4} />
				</div>
			)}
		</ConnectorPageColumn>
	);
}
